import React, { useState } from 'react';
import classNames from 'classnames';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  formatActionDate,
  fromDateTimeLocalInput,
  isActionOverdue,
  toDateTimeLocalInput,
} from './actionEditModel';
import type { ActionEntity } from '../hooks/useScopedActionForm';

type DueDateCellProps = {
  action: ActionEntity;
  /** Which date this cell edits; both are stored as UTC instants. */
  field: 'start_date' | 'target_date';
  onChange: (patch: Record<string, unknown>) => void;
  disabled?: boolean;
};

/**
 * The draft lives here rather than in the cell so that remounting this on a new
 * stored value is all it takes to reset it. It is only committed on Save,
 * because a `datetime-local` input fires `change` for every intermediate value
 * the user types and each of those would be a full-entity PUT.
 */
const DateEditor: React.FC<{
  id: string;
  label: string;
  initial: string;
  canClear: boolean;
  onSave: (value: string | null) => void;
  onClear: () => void;
}> = ({ id, label, initial, canClear, onSave, onClear }) => {
  const [draft, setDraft] = useState(initial);

  return (
    <>
      <Label className="tw:mb-1" htmlFor={id}>
        {label}
      </Label>
      <Input
        id={id}
        type="datetime-local"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <div className="tw:mt-2 tw:flex tw:gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => onSave(fromDateTimeLocalInput(draft))}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onClear}
          disabled={!canClear}
        >
          Clear
        </Button>
      </div>
    </>
  );
};

/**
 * A date cell that reads as text and opens a small editor rather than sitting
 * in the table as a permanently-focusable input, which at a few hundred rows
 * makes keyboard traversal unusable.
 */
export const DueDateCell: React.FC<DueDateCellProps> = ({
  action,
  field,
  onChange,
  disabled = false,
}) => {
  const stored = action[field] ?? null;
  const [open, setOpen] = useState(false);

  const overdue =
    field === 'target_date' && isActionOverdue(action.status, stored);

  const commit = (next: string | null) => {
    setOpen(false);
    onChange({ [field]: next });
  };

  const label = formatActionDate(stored);

  if (disabled) {
    return (
      <span className={classNames({ 'tw:text-destructive': overdue })}>
        {label}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={classNames('inline-edit-cell', {
          'inline-edit-cell--empty': !stored,
        })}
        aria-label={`${field === 'target_date' ? 'Due date' : 'Start date'} for ${
          action.name || 'this task'
        }`}
        data-action-field={field}
      >
        <span
          className={classNames('text-truncate', {
            'tw:text-destructive': overdue,
          })}
        >
          {label}
        </span>
      </PopoverTrigger>
      <PopoverContent className="dark inline-edit-date tw:w-64 tw:gap-2">
        <DateEditor
          key={String(stored)}
          id={`${field}-${action.id}`}
          label={field === 'target_date' ? 'Due' : 'Start'}
          initial={toDateTimeLocalInput(stored)}
          canClear={Boolean(stored)}
          onSave={commit}
          onClear={() => commit(null)}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DueDateCell;
